import React, { createContext, useContext, useState, useCallback, useEffect, ReactNode } from 'react';
import ApiService from '@/services/api';
import { logger } from '@/utils/logger';
import {
  IEmployeePublic,
  Role,
  Permission,
  hasPermission,
  hasAnyPermission,
  hasAllPermissions,
  canAccessRole,
  isInternalRole,
} from '@/types/auth';

interface AuthContextType {
  user: IEmployeePublic | null;
  role: Role | null;
  permissions: Permission[];
  isAuthenticated: boolean;
  isLoading: boolean;
  login: (username: string, password: string) => Promise<boolean>;
  loginWithGoogle: (credential: string) => Promise<boolean>;
  verifyPasscode: (passcode: string) => Promise<boolean>;
  logout: () => void;
  // Permission helpers
  can: (permission: Permission) => boolean;
  canAny: (permissions: Permission[]) => boolean;
  canAll: (permissions: Permission[]) => boolean;
  hasRole: (requiredRole: Role) => boolean;
  isInternal: () => boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<IEmployeePublic | null>(null);
  const [role, setRole] = useState<Role | null>(null);
  const [permissions, setPermissions] = useState<Permission[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  // Check for existing token on mount
  useEffect(() => {
    const initAuth = async () => {
      const token = localStorage.getItem('auth_token');
      if (token) {
        try {
          const response = await ApiService.getCurrentUser();
          setUser(response.user);
          setRole(response.role);
          setPermissions(response.permissions as Permission[]);
        } catch {
          // Token invalid or expired, clear it
          localStorage.removeItem('auth_token');
          setUser(null);
          setRole(null);
          setPermissions([]);
        }
      }
      setIsLoading(false);
    };
    initAuth();
  }, []);

  const login = useCallback(async (username: string, password: string): Promise<boolean> => {
    try {
      const response = await ApiService.login({ username, password });
      setUser(response.user);
      setRole(response.role);
      setPermissions(response.permissions as Permission[]);
      return true;
    } catch (error) {
      logger.error('Login failed:', error);
      return false;
    }
  }, []);

  const loginWithGoogle = useCallback(async (credential: string): Promise<boolean> => {
    try {
      const response = await ApiService.loginWithGoogle({ credential });
      setUser(response.user);
      setRole(response.role);
      setPermissions(response.permissions as Permission[]);
      return true;
    } catch (error) {
      logger.error('Google login failed:', error);
      return false;
    }
  }, []);

  const verifyPasscode = useCallback(async (passcode: string): Promise<boolean> => {
    try {
      const response = await ApiService.verifyPasscode({ passcode });
      return response.verified;
    } catch (error) {
      logger.error('Passcode verification failed:', error);
      return false;
    }
  }, []);

  const logout = useCallback(() => {
    ApiService.logout();
    setUser(null);
    setRole(null);
    setPermissions([]);
  }, []);

  // Permission helper: check if user has a specific permission
  const can = useCallback((permission: Permission): boolean => {
    return hasPermission(permissions, permission);
  }, [permissions]);

  // Permission helper: check if user has any of the permissions
  const canAny = useCallback((requiredPermissions: Permission[]): boolean => {
    return hasAnyPermission(permissions, requiredPermissions);
  }, [permissions]);

  // Permission helper: check if user has all of the permissions
  const canAll = useCallback((requiredPermissions: Permission[]): boolean => {
    return hasAllPermissions(permissions, requiredPermissions);
  }, [permissions]);

  // Role helper: check if user's role level meets the required role
  const hasRole = useCallback((requiredRole: Role): boolean => {
    if (!role) return false;
    return canAccessRole(role, requiredRole);
  }, [role]);

  // Check if user is an internal employee (not customer/readonly)
  const isInternal = useCallback((): boolean => {
    if (!role) return false;
    return isInternalRole(role);
  }, [role]);

  return (
    <AuthContext.Provider
      value={{
        user,
        role,
        permissions,
        isAuthenticated: !!user,
        isLoading,
        login,
        loginWithGoogle,
        verifyPasscode,
        logout,
        can,
        canAny,
        canAll,
        hasRole,
        isInternal,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
